import { TrajectoryChart, TrajectoryLegend } from "@/charts/TrajectoryChart";
import { EntityKind } from "@/generated/core/v1/financial_plan";
import { ChartBox } from "@/components/ChartInfo";
import { Headline } from "@/components/Headline";
import { formatDate } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import {
  CASH_KINDS,
  FIRST_DAY_NOTE,
  firstState,
  INVESTMENT_KINDS,
  KIND_LABEL,
  nameWithRate,
  netWorthSeries,
  netWorthToday,
  oweTotal,
  ownTotal,
  retirementMarker,
} from "@/plan-logic/plan-view";
import { PlanPage, type PlanCtx } from "../PlanPage";

type Row = [string, number, string];

/** The prototype's breakdown bars (`bd`). */
export function Breakdown({ rows, testId }: { rows: Row[]; testId: string }) {
  if (!rows.length) return <p className="mut">Nothing here.</p>;
  const max = Math.max(...rows.map((r) => r[1]));
  const tot = rows.reduce((a, r) => a + r[1], 0);
  return (
    <div data-testid={testId}>
      {rows.map(([n, v, c]) => (
        <div className="catrow" key={n}>
          <span className="nm">{n}</span>
          <span className="tr">
            <span
              className="fl"
              style={{ width: `${max ? (v / max) * 100 : 0}%`, background: c }}
            />
          </span>
          <span className="vl">{money(v)}</span>
          <span className="pc">{tot ? Math.round((v / tot) * 100) : 0}%</span>
        </div>
      ))}
      <div className="row" style={{ borderTop: "2px solid var(--line)", marginTop: 8 }}>
        <b style={{ flex: 1 }}>Total</b>
        <span className="amt">{money(tot)}</span>
      </div>
    </div>
  );
}

const ASSET_COLOR: Partial<Record<EntityKind, string>> = {
  [EntityKind.PROPERTY]: "var(--ess)",
  [EntityKind.CHEQUING]: "var(--life)",
  [EntityKind.SAVINGS]: "var(--life)",
};

/**
 * "Where it's heading": net worth on Abi's plan and on the current path. Exported for the
 * marketing site, which renders it from the test fixtures ("See both routes").
 */
export function NetWorthHeadingCard({
  abi,
  naive,
  retirement,
}: {
  abi: PlanCtx["abi"];
  naive?: PlanCtx["naive"];
  /** The retirement marker (plan-view `retirementMarker`); the site's preview leaves it out. */
  retirement?: { x: number; year: number };
}) {
  const series = netWorthSeries(abi);
  return (
    <div className="card pad" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", marginBottom: 10 }}>
        <h2>Where it&rsquo;s heading</h2>
        <span className="mut" style={{ marginLeft: "auto" }}>
          Today to {series.at(-1)?.date.year}
        </span>
      </div>
      <TrajectoryLegend naive={!!naive} />
      <ChartBox id="nwbig" style={{ marginTop: 10 }}>
        <TrajectoryChart
          abi={series}
          naive={naive ? netWorthSeries(naive) : undefined}
          height={300}
          retirement={retirement}
        />
      </ChartBox>
    </div>
  );
}

function Body({ ctx }: { ctx: PlanCtx }) {
  const { abi, naive, index, family } = ctx;
  const s0 = firstState(abi);
  const own = ownTotal(s0);
  const owe = oweTotal(s0);
  const debts = (s0?.debts ?? []).filter((d) => d.balance > 0.5);

  // What you own: cash together, then each account and property.
  const ownRows: Row[] = [];
  const held = [...(s0?.accounts ?? []), ...(s0?.assets ?? [])];
  const cash = held.filter((b) => CASH_KINDS.has(b.kind)).reduce((a, b) => a + b.balance, 0);
  held
    .filter((b) => !CASH_KINDS.has(b.kind) && b.balance > 0.5)
    .sort((a, b) => b.balance - a.balance)
    .forEach((b) => {
      const info = index.get(b.entityUuid);
      const label =
        b.kind === EntityKind.PROPERTY
          ? (info?.name ?? "Home")
          : !info || info.name === KIND_LABEL[b.kind]
            ? KIND_LABEL[b.kind]
            : info.name.includes(KIND_LABEL[b.kind])
              ? info.name // "Alex’s TFSA"
              : `${KIND_LABEL[b.kind]}, ${info.name}`;
      ownRows.push([label, b.balance, ASSET_COLOR[b.kind] ?? "var(--save)"]);
    });
  if (cash > 0.5) ownRows.push(["Cash", cash, "var(--life)"]);
  // Two accounts of the same kind and name would collide as keys; make labels unique.
  const seen = new Map<string, number>();
  ownRows.forEach((r) => {
    const n = (seen.get(r[0]) ?? 0) + 1;
    seen.set(r[0], n);
    if (n > 1) r[0] = `${r[0]} (${n})`;
  });

  const oweRows: Row[] = debts
    .sort((a, b) => b.balance - a.balance)
    .map((d) => [
      nameWithRate(index.get(d.entityUuid), KIND_LABEL[d.kind]),
      d.balance,
      "var(--debt)",
    ]);

  const owned = [
    cash > 0.5 && "cash",
    held.some((b) => INVESTMENT_KINDS.has(b.kind) && b.balance > 0.5) && "investments",
    held.some((b) => b.kind === EntityKind.PROPERTY && b.balance > 0.5) && "property",
  ].filter((x): x is string => !!x);
  const kinds = [...new Set(debts.map((d) => KIND_LABEL[d.kind].toLowerCase()))];

  return (
    <>
      {/* v45: no Abi number here on purpose; the chart underneath is the Abi number. */}
      <Headline
        id="hd-nw"
        cards={[
          { k: "What you own", v: money(own), s: owned.length ? andList(owned) : "nothing yet" },
          {
            k: "What you owe",
            v: money(owe),
            tone: owe > 0.5 ? "debt" : undefined,
            s: kinds.length ? andList(kinds) : "nothing",
          },
          {
            k: "Net worth",
            v: money(netWorthToday(abi)),
            s: `the difference, at the end of ${formatDate(ctx.planStart)}`,
            testId: "nw-total",
          },
        ]}
      />

      <p className="hint" data-testid="nw-first-day" style={{ maxWidth: "74ch", marginTop: -6 }}>
        {FIRST_DAY_NOTE.replace("{date}", formatDate(ctx.planStart))}
      </p>

      <NetWorthHeadingCard abi={abi} naive={naive} retirement={retirementMarker(abi, family)} />

      <div className="grid2" style={{ marginBottom: 16 }}>
        <div className="card pad">
          <h2 style={{ marginBottom: 14 }}>What you own</h2>
          <div id="assets">
            <Breakdown rows={ownRows} testId="nw-assets" />
          </div>
        </div>
        <div className="card pad">
          <h2 style={{ marginBottom: 14 }}>What you owe</h2>
          <div id="nw-debts">
            <Breakdown rows={oweRows} testId="nw-debts" />
          </div>
        </div>
      </div>
    </>
  );
}

/** "a, b and c". */
function andList(xs: string[]): string {
  return xs.length < 2 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`;
}

export function NetWorthPage() {
  return (
    <PlanPage title="pages.networth.title" sub="pages.networth.sub" testId="networth">
      {(ctx) => <Body ctx={ctx} />}
    </PlanPage>
  );
}
